import Guid from "_shared/guids/guid";

/**
 * What each selected template is responsible for, keyed by template id.
 *
 * Selecting a team or fee schedule template merges its contents into a collection the user may
 * already have populated by hand, so "what the template contains" and "what the template is
 * responsible for" are not the same set: an item that was already there before the box was checked
 * belongs to the user. Deselecting has to remove only what checking the box put there (#4486), and
 * nothing persisted records that provenance — team members and fee schedule categories carry no
 * template id the way `WorkDocument.templateIds` does — so it is remembered here for the life of
 * the mounted tab.
 *
 * A template that was already checked when the tab mounted therefore has no record, and deselecting
 * it removes nothing. That is deliberate: with no record, "remove the template's items" and "remove
 * the items the user added by hand" are indistinguishable, and silently deleting the user's own
 * work is the worse failure.
 */
export type TemplateContribution = Readonly<{
  /**
   * The item keys checking this template's box actually ADDED. Only these can ever be taken back.
   */
  addedItemKeys: readonly string[];
  /**
   * Every item key this template supplies, whether it added them or found them already there. This
   * is what keeps a shared item alive while any template that contains it is still checked, and it
   * is recorded per template rather than re-read from the loaded template list because that list is
   * one ownership-filtered page: a still-selected template on another page or under another
   * ownership scope is simply absent from it.
   */
  suppliedItemKeys: readonly string[];
}>;

export type TemplateContributions = Readonly<Record<string, TemplateContribution>>;

export const NO_TEMPLATE_CONTRIBUTIONS: TemplateContributions = {};

/**
 * Record what a template selection supplied and what of that it added. `addedItemKeys` is expected
 * to be a subset of `suppliedItemKeys` — the items the template contains that were not already in
 * the collection.
 *
 * Also worth calling for a template the tab itself creates out of the current collection: it added
 * nothing (every item was already there), but it supplies all of them, so recording it stops a
 * later deselection of some other template from taking those items away.
 */
export function withTemplateSelected(args: {
  contributions: TemplateContributions;
  templateId: Guid;
  suppliedItemKeys: readonly string[];
  addedItemKeys: readonly string[];
}): TemplateContributions {
  const { contributions, templateId, suppliedItemKeys, addedItemKeys } = args;
  return {
    ...contributions,
    [templateId.value]: {
      addedItemKeys: [...addedItemKeys],
      suppliedItemKeys: [...suppliedItemKeys],
    },
  };
}

/**
 * Forget a deselected template and say which of its items go with it.
 *
 * An item this template added but another still-selected template also supplies stays, because that
 * other checkbox is still ticked — and responsibility for it TRANSFERS to those templates. Without
 * the transfer the item would outlive every template that supplies it: deselecting the second
 * template would find no record of having added it (it was already there when its box was checked)
 * and leave it behind for good.
 *
 * `alsoSuppliedItemKeys` is a best-effort supplement for templates that were already checked when
 * the tab mounted and so have no record — pass what the loaded template list says they contain. It
 * can only ever keep an item, never remove one.
 *
 * Callers intersect `itemKeysToRemove` with what is actually in the collection — an item the
 * template added and the user has since deleted by hand must not be removed a second time.
 */
export function withTemplateDeselected(args: {
  contributions: TemplateContributions;
  templateId: Guid;
  alsoSuppliedItemKeys?: readonly string[];
}): { contributions: TemplateContributions; itemKeysToRemove: Set<string> } {
  const { contributions, templateId, alsoSuppliedItemKeys = [] } = args;
  const contribution = contributions[templateId.value];
  if (!contribution) return { contributions, itemKeysToRemove: new Set() };

  const remaining = Object.entries(contributions).filter(([id]) => id !== templateId.value);
  const suppliedElsewhere = new Set(alsoSuppliedItemKeys);
  for (const [, other] of remaining) {
    other.suppliedItemKeys.forEach((itemKey) => suppliedElsewhere.add(itemKey));
  }

  const itemKeysToRemove = new Set<string>();
  const retainedItemKeys = new Set<string>();
  for (const itemKey of contribution.addedItemKeys) {
    if (suppliedElsewhere.has(itemKey)) retainedItemKeys.add(itemKey);
    else itemKeysToRemove.add(itemKey);
  }

  const updated: Record<string, TemplateContribution> = {};
  for (const [id, other] of remaining) {
    const alreadyOwned = new Set(other.addedItemKeys);
    const inherited = other.suppliedItemKeys.filter(
      (itemKey) => retainedItemKeys.has(itemKey) && !alreadyOwned.has(itemKey),
    );
    updated[id] = inherited.length
      ? { ...other, addedItemKeys: [...other.addedItemKeys, ...inherited] }
      : other;
  }

  return { contributions: updated, itemKeysToRemove };
}
